import { TextElementController, DataStateChangeEvent } from '@sas-dvr/ltjs-bird';
import { merge, from } from 'rxjs';
import { VisualElementControllerLoader } from './LoaderFactory';
import { TextState } from '@sas-dvr/internal-va-react-core/store/birdElement/textState';
import { parseHTMLString, createRichTextHeightCache } from '../util/birdRichText';
import { fromPortableEvent, getOrCreateLayoutConstraint, withRetained } from '../util';
import { tap, ignoreElements, mergeMap } from 'rxjs/operators';
import {
  PreferredSizeRequestEvent,
  Dimension,
  DimensionUnit,
  PreferredSizeBehaviorType,
  FlexibilityType,
  LayoutProxy,
} from '@sas-dvr/ltjs-commons';
import { ofActionCreatorType } from '../util/rxUtils';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { loadDataVisualElementController } from './loadDataVisualElement';
import { getAllTextStyles } from '@sas-dvr/ltjs-commons/util/StyleUtils';
import cn from '@sas-dvr/internal-va-react-core/components/BirdText/BirdText.css.json';

export const loadTextElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(visualElementController instanceof TextElementController)) {
    throw new Error('Expected the controller to be a TextElementController');
  }
  const id = deps.getKey(visualElementController);

  const layoutProxy = visualController?.getLayoutProxy();
  if (!layoutProxy) {
    throw new Error('layoutProxy should not be null');
  }

  const { measureHeight } = createRichTextHeightCache();

  // HEIGHT_REQUEST does not get fired for elements in precision containers
  const relativelyPositioned = isRelativelyPositioned(layoutProxy);
  const eventType = relativelyPositioned
    ? PreferredSizeRequestEvent.PREFERRED_HEIGHT_REQUEST
    : PreferredSizeRequestEvent.HEIGHT_REQUEST;

  const handleHeight$ = fromPortableEvent<PreferredSizeRequestEvent>(layoutProxy, eventType).pipe(
    withRetained(layoutProxy),
    tap((e) => {
      const layoutConstraint = getOrCreateLayoutConstraint(layoutProxy);
      let width = e.getConstrainedSize();

      // Padding and border is included in the constrained size for visuals in relative layouts only
      if (relativelyPositioned) {
        const padding =
          layoutConstraint.getPadding()?.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0) ??
          0;
        const border = layoutConstraint.getBorderThickness();
        width -= padding * 2 + border * 2;
      }

      const styleChain = visualElementController.getSpanStyleChain(null, null);
      const textStyles = styleChain ? getAllTextStyles(styleChain, true) : null;

      const height = measureHeight(visualElementController.getRichTextAsHTML(), width, cn.text, {
        '--text-font-size': textStyles?.['font-size'] ?? '0.875rem',
        ...textStyles,
      });
      if (
        layoutConstraint.getHeightPreferredSizeBehavior() === PreferredSizeBehaviorType.HONORSIZE
      ) {
        layoutConstraint.setHeight(new Dimension(height, DimensionUnit.PIXELS));
      }
      const isShrinkable = layoutConstraint.getHeightFlexibility() === FlexibilityType.SHRINKABLE;
      const minHeight = isShrinkable ? Math.min(height, 180) : height;
      layoutConstraint.setMinimumHeight(minHeight);
    }),
    ignoreElements()
  );

  //
  // VisualElementController load
  //
  const loadVisualElement$ = loadDataVisualElementController(
    visualController,
    visualElementController,
    unload$,
    myActions$,
    state$,
    deps,
    {
      create: () =>
        TextState.actions.add(id, {
          text: parseHTMLString(visualElementController.getRichTextAsHTML()),
        }),
      delete: () => TextState.actions.delete(id),
    }
  );

  const notifyInteraction$ = myActions$.pipe(
    ofActionCreatorType<typeof TextState.actions.notifyInteraction>(
      TextState.actionTypes.notifyInteraction
    ),
    tap((action) => {
      visualElementController.triggerInteraction(action.payload.data);
    }),
    ignoreElements()
  );

  const handleDataChange$ = fromPortableEvent(
    visualElementController,
    DataStateChangeEvent.DATACHANGE
  ).pipe(
    mergeMap(() =>
      from([
        TextState.actions.update(id, {
          text: parseHTMLString(visualElementController.getRichTextAsHTML()),
        }),
        BirdLayoutState.actions.notifyInvalidateLayout(),
      ])
    )
  );

  return merge(loadVisualElement$, notifyInteraction$, handleDataChange$, handleHeight$); //, handlePreferredHeight$);
};

// Implementation borrowed from ResponsiveLayoutHelper::isRelativeLayout()
function isRelativelyPositioned(layoutProxy: LayoutProxy): boolean {
  const constraint = layoutProxy.getConstraint();
  const relativelyPositioned = constraint?.getHorizontalOffset() && constraint?.getVerticalOffset();
  return !!relativelyPositioned;
}
